import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { format } from "date-fns";
import InfiniteScroll from "react-infinite-scroll-component";
import { debounce } from "lodash";
import useMediaQuery from "@mui/material/useMediaQuery";
import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Drawer from "@mui/material/Drawer";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/HighlightOff";
import FilteredIcon from "@mui/icons-material/FilterAlt";
import SearchContactCard from "./search-contact-card";
import SearchCampaignCard from "./search-campaign-card";
import SearchKnowledgeCard from "./search-knowledge-card";
import SearchTicketCard from "./search-ticket-card";
import SearchUserCard from "./search-user-card";
import ContactFilters from "../contacts/contact-filters";
import TicketFilters from "../tickets/ticket-filters";
import { useTenancy } from "../../../services/tenancy";
import { useRoles } from "../../../services/roles";
import { useApiTenant } from "../../../api/api-tenant";
import { cleanText } from "../../../helpers/clean";
export default function Search() {
    const { t } = useTranslation();
    const location = useLocation();
    const tenancy = useTenancy();
    const api = useApiTenant();
    const roles = useRoles();
    const defaultSearchMeta = {
        current_page: 0,
        total: 0,
    };
    const defaultSearchOptions = {
        search_for: "tickets",
        search_contacts_by: process.env.APP_ID === "mcd" ? "id" : "phones",
        filter_contact_type: null,
        filter_contact_phone_country: null,
        filter_contact_phone_area: null,
        filter_contact_custom_fields: null,
        search_tickets_by: process.env.APP_ID === "mcd" ? "id" : "contact.phones",
        filter_campaigns: null,
        filter_contacts: null,
        filter_statuses: null,
        filter_priorities: null,
        filter_assignees: null,
        filter_groups: null,
        filter_created_by_ids: null,
        filter_updated_by_ids: null,
        filter_draft: "exclude",
        filter_unassigned: "include",
        filter_ungrouped: "include",
        filter_created_at_start_date: null,
        filter_created_at_end_date: null,
        filter_created_at_start_date_custom: null,
        filter_created_at_end_date_custom: null,
        filter_created_at_start_time_custom: null,
        filter_created_at_end_time_custom: null,
        filter_updated_at_start_date: null,
        filter_updated_at_end_date: null,
        filter_updated_at_start_time: null,
        filter_updated_at_end_time: null,
        filter_start_date_from: null,
        filter_start_date_to: null,
        filter_start_time_from: null,
        filter_start_time_to: null,
        filter_due_date_from: null,
        filter_due_date_to: null,
        filter_due_time_from: null,
        filter_due_time_to: null,
        filter_ticket_custom_fields: null,
        filter_contact_is_new: "include",
        filter_inbound_channels: null,
        filter_outbound_channels: null,
        filter_inbound_social_channels: null,
        filter_outbound_social_channels: null,
        filter_forwarded_call: null,
    };
    const [search, setSearch] = useState("");
    const [searchFor, setSearchFor] = useState("tickets");
    const [searchResults, setSearchResults] = useState([]);
    const [searchMeta, setSearchMeta] = useState(defaultSearchMeta);
    const [searchOptions, setSearchOptions] = useState(defaultSearchOptions);
    const [isMenuOpen, setMenuOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showContactFilters, setShowContactFilters] = useState(false);
    const [searchContactsBy, setSearchContactsBy] = useState(process.env.APP_ID === "mcd" ? "id" : "phones");
    const [filterContactType, setFilterContactType] = useState(null);
    const [filterContactPhoneCountry, setFilterContactPhoneCountry] = useState(null);
    const [filterContactPhoneArea, setFilterContactPhoneArea] = useState(null);
    const [filterContactCustomFields, setFilterContactCustomFields] = useState([]);
    const [showTicketFilters, setShowTicketFilters] = useState(false);
    const [searchTicketsBy, setSearchTicketsBy] = useState(process.env.APP_ID === "mcd" ? "id" : "contact.phones");
    const [filterTicketsByCampaigns, setFilterTicketsByCampaigns] = useState([]);
    const [filterTicketsByContacts, setFilterTicketsByContacts] = useState([]);
    const [filterTicketsByStatuses, setFilterTicketsByStatuses] = useState([]);
    const [filterTicketsByPriorities, setFilterTicketsByPriorities] = useState([]);
    const [filterTicketsByAssignees, setFilterTicketsByAssignees] = useState([]);
    const [filterTicketsByGroups, setFilterTicketsByGroups] = useState([]);
    const [filterTicketsByCreatedBy, setFilterTicketsByCreatedBy] = useState([]);
    const [filterTicketsByUpdatedBy, setFilterTicketsByUpdatedBy] = useState([]);
    const [filterDrafts, setFilterDrafts] = useState("exclude");
    const [filterTicketsByUnassigned, setFilterTicketsByUnassigned] = useState("include");
    const [filterTicketsByUngrouped, setFilterTicketsByUngrouped] = useState("include");
    const [filterTicketsByCreatedAtStart, setFilterTicketsByCreatedAtStart] = useState(undefined);
    const [filterTicketsByCreatedAtEnd, setFilterTicketsByCreatedAtEnd] = useState(undefined);
    const [filterTicketsByCreatedAtStartDateCustom, setFilterTicketsByCreatedAtStartDateCustom] = useState(undefined);
    const [filterTicketsByCreatedAtEndDateCustom, setFilterTicketsByCreatedAtEndDateCustom] = useState(undefined);
    const [filterTicketsByCreatedAtStartTimeCustom, setFilterTicketsByCreatedAtStartTimeCustom] = useState(undefined);
    const [filterTicketsByCreatedAtEndTimeCustom, setFilterTicketsByCreatedAtEndTimeCustom] = useState(undefined);
    const [filterTicketsByUpdatedAtStartDate, setFilterTicketsByUpdatedAtStartDate] = useState(undefined);
    const [filterTicketsByUpdatedAtEndDate, setFilterTicketsByUpdatedAtEndDate] = useState(undefined);
    const [filterTicketsByUpdatedAtStartTime, setFilterTicketsByUpdatedAtStartTime] = useState(undefined);
    const [filterTicketsByUpdatedAtEndTime, setFilterTicketsByUpdatedAtEndTime] = useState(undefined);
    const [filterTicketsByStartStartDate, setFilterTicketsByStartStartDate] = useState(undefined);
    const [filterTicketsByStartEndDate, setFilterTicketsByStartEndDate] = useState(undefined);
    const [filterTicketsByStartStartTime, setFilterTicketsByStartStartTime] = useState(undefined);
    const [filterTicketsByStartEndTime, setFilterTicketsByStartEndTime] = useState(undefined);
    const [filterTicketsByDueStartDate, setFilterTicketsByDueStartDate] = useState(undefined);
    const [filterTicketsByDueEndDate, setFilterTicketsByDueEndDate] = useState(undefined);
    const [filterTicketsByDueStartTime, setFilterTicketsByDueStartTime] = useState(undefined);
    const [filterTicketsByDueEndTime, setFilterTicketsByDueEndTime] = useState(undefined);
    const [filterTicketCustomFields, setFilterTicketCustomFields] = useState([]);
    const [filterCampaignChoices, setFilterCampaignChoices] = useState([]);
    const [filterContactIsNew, setFilterContactIsNew] = useState("include");
    const [filterInboundChannels, setFilterInboundChannels] = useState([]);
    const [filterOutboundChannels, setFilterOutboundChannels] = useState([]);
    const [filterInboundSocialChannels, setFilterInboundSocialChannels] = useState([]);
    const [filterOutboundSocialChannels, setFilterOutboundSocialChannels] = useState([]);
    const [filterForwardedCalls, setFilterForwardedCalls] = useState("include");
    const [timeFrame, setTimeFrame] = useState("total");
    const [filter, setFilter] = useState(false);
    const [showMoreTicketFilters, setShowMoreTicketFilters] = useState(false);
    const [showFieldFilters, setShowFieldFilters] = useState(false);
    const [showChannelFilters, setShowChannelFilters] = useState(false);
    const [showMoreContactFilters, setShowMoreContactFilters] = useState(false);
    const [areFieldsAndGroupsLoaded, setAreFieldsAndGroupsLoaded] = useState(false);
    const [groups, setGroups] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [filterableChoices, setFilterableChoices] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [audiences, setAudiences] = useState([]);
    const [phoneCountries, setPhoneCountries] = useState([]);
    const [phoneAreas, setPhoneAreas] = useState([]);
    const [contactCustomFields, setContactCustomFields] = useState([]);
    const isMobile = useMediaQuery("(max-width: 767px)");
    const useStyles = makeStyles({
        tabs: {
            "& .MuiTab-root": {
                minWidth: "100px",
            },
            "& .MuiTabs-scrollButtons.Mui-disabled": {
                opacity: 0.3,
            },
        },
        scroll: {
            "& .infinite-scroll-component__outerdiv": {
                width: "100%",
            },
        },
    });
    const classes = useStyles();
    useEffect(() => {
        if (location.pathname.includes("/login"))
            return;
        setMenuOpen(false);
    }, [location]);
    useEffect(() => {
        setSearch("");
        setSearchResults([]);
        setSearchMeta(defaultSearchMeta);
    }, [tenancy.client]);
    useEffect(() => {
        setSearchOptions({
            search_for: searchFor,
            search_contacts_by: searchContactsBy,
            filter_contact_type: filterContactType,
            filter_contact_phone_country: filterContactPhoneCountry,
            filter_contact_phone_area: filterContactPhoneArea,
            filter_contact_custom_fields: filterContactCustomFields,
            search_tickets_by: searchTicketsBy,
            filter_campaigns: filterTicketsByCampaigns,
            filter_contacts: filterTicketsByContacts,
            filter_statuses: filterTicketsByStatuses,
            filter_priorities: filterTicketsByPriorities,
            filter_assignees: filterTicketsByAssignees,
            filter_groups: filterTicketsByGroups,
            filter_created_by_ids: filterTicketsByCreatedBy,
            filter_updated_by_ids: filterTicketsByUpdatedBy,
            filter_draft: filterDrafts,
            filter_unassigned: filterTicketsByUnassigned,
            filter_ungrouped: filterTicketsByUngrouped,
            filter_created_at_start_date: filterTicketsByCreatedAtStart !== "" ? filterTicketsByCreatedAtStart : null,
            filter_created_at_start_date_custom: filterTicketsByCreatedAtStartDateCustom,
            filter_created_at_start_time_custom: filterTicketsByCreatedAtStartTimeCustom,
            filter_created_at_end_date: filterTicketsByCreatedAtEnd
                ? filterTicketsByCreatedAtEnd
                : null,
            filter_created_at_end_date_custom: filterTicketsByCreatedAtEndDateCustom,
            filter_created_at_end_time_custom: filterTicketsByCreatedAtEndTimeCustom,
            filter_updated_at_start_date: filterTicketsByUpdatedAtStartDate,
            filter_updated_at_end_date: filterTicketsByUpdatedAtEndDate,
            filter_updated_at_start_time: filterTicketsByUpdatedAtStartTime,
            filter_updated_at_end_time: filterTicketsByUpdatedAtEndTime,
            filter_start_date_from: filterTicketsByStartStartDate,
            filter_start_date_to: filterTicketsByStartEndDate,
            filter_start_time_from: filterTicketsByStartStartTime,
            filter_start_time_to: filterTicketsByStartEndTime,
            filter_due_date_from: filterTicketsByDueStartDate,
            filter_due_date_to: filterTicketsByDueEndDate,
            filter_due_time_from: filterTicketsByDueStartTime,
            filter_due_time_to: filterTicketsByDueEndTime,
            filter_ticket_custom_fields: filterTicketCustomFields,
            filter_contact_is_new: filterContactIsNew,
            filter_inbound_channels: filterInboundChannels,
            filter_outbound_channels: filterOutboundChannels,
            filter_inbound_social_channels: filterInboundSocialChannels,
            filter_outbound_social_channels: filterOutboundSocialChannels,
            filter_forwarded_call: filterForwardedCalls,
        });
        if (filter) {
            setFilter(false);
            if (showTicketFilters && search === "")
                setShowTicketFilters(false);
            if (showContactFilters && search === "")
                setShowContactFilters(false);
        }
    }, [searchFor, searchContactsBy, filter]);
    useEffect(() => {
        setSearchResults([]);
        setSearchMeta(defaultSearchMeta);
        if (!tenancy.client || search.length === 0)
            return;
        setIsLoading(true);
        debounceSearch(cleanText(search), defaultSearchMeta, searchOptions);
    }, [search, searchOptions]);
    const searchByScrolling = () => {
        if (searchFor === "contacts")
            getContacts(cleanText(search), searchMeta, searchOptions);
        else if (searchFor === "campaigns")
            getCampaigns(cleanText(search), searchMeta);
        else if (searchFor === "tickets")
            getTickets(cleanText(search), searchMeta, searchOptions);
    };
    const getContacts = (search, searchMeta, searchOptions) => {
        setIsLoading(true);
        let ids = "";
        let values = "";
        if (searchOptions["filter_contact_custom_fields"].length > 0) {
            searchOptions["filter_contact_custom_fields"].map((cf, index) => {
                cf.chosenItems.map((item, index) => {
                    if (item.id) {
                        values =
                            values +
                                item.id.toString() +
                                (index !== cf.chosenItems.length - 1 ? "|" : "");
                    }
                    else {
                        values =
                            values + item.value + (index !== cf.chosenItems.length - 1 ? "|" : "");
                    }
                });
                values =
                    values +
                        (index !== searchOptions["filter_contact_custom_fields"].length - 1 ? "," : "");
                ids =
                    ids +
                        cf.customFieldId +
                        (index !== searchOptions["filter_contact_custom_fields"].length - 1 ? "," : "");
            });
        }
        api.GET_contacts(10, searchMeta.current_page + 1, "id", "asc", search, searchOptions["search_contacts_by"] && search
            ? searchOptions["search_contacts_by"]
            : undefined, searchOptions["filter_contact_type"] ? searchOptions["filter_contact_type"] : undefined, searchOptions["filter_contact_phone_country"]
            ? searchOptions["filter_contact_phone_country"].id
            : undefined, searchOptions["filter_contact_phone_area"]
            ? searchOptions["filter_contact_phone_area"].id
            : undefined, undefined, undefined, ids !== "" ? ids : undefined, values !== "" ? values : undefined)
            .then(response => {
            setSearchResults(searchResults.concat(response.data.contacts));
            let total = response.data.contacts.length === parseInt(response.data.meta.per_page)
                ? 100000000
                : response.data.meta.current_page === 1
                    ? response.data.contacts.length
                    : response.data.contacts.length +
                        parseInt(response.data.meta.per_page) *
                            (response.data.meta.current_page - 1);
            setSearchMeta({
                current_page: response.data.meta.current_page,
                total: total,
            });
            setShowContactFilters(false);
        })
            .catch(error => {
            console.error(error);
        })
            .finally(() => {
            setIsLoading(false);
        });
    };
    const getTickets = (search, searchMeta, searchOptions) => {
        setIsLoading(true);
        api.GET_tickets(10, searchMeta.current_page + 1, "id", "desc", search, searchOptions["search_tickets_by"] ? searchOptions["search_tickets_by"] : undefined, searchOptions["filter_campaigns"]
            ? searchOptions["filter_campaigns"].map(campaign => campaign.id)
            : undefined, searchOptions["filter_contacts"]
            ? searchOptions["filter_contacts"].map(contact => contact.id)
            : undefined, searchOptions["filter_status_ids"]
            ? searchOptions["filter_statuses"].map(status => status.id)
            : undefined, searchOptions["filter_priorities"]
            ? searchOptions["filter_priorities"].map(priority => priority.id)
            : undefined, searchOptions["filter_assignees"]
            ? searchOptions["filter_assignees"].map(assignee => assignee.id)
            : undefined, searchOptions["filter_groups"] > 0
            ? searchOptions["filter_groups"].map(group => group.id)
            : ["-1"], searchOptions["filter_created_by_ids"]
            ? searchOptions["filter_created_by_ids"]
            : undefined, searchOptions["filter_updated_by_ids"]
            ? searchOptions["filter_updated_by_ids"]
            : undefined, searchOptions["filter_draft"] ? searchOptions["filter_draft"] : undefined, searchOptions["filter_unassigned"] ? searchOptions["filter_unassigned"] : undefined, searchOptions["filter_ungrouped"] ? searchOptions["filter_ungrouped"] : undefined, searchOptions["filter_created_at_start_date_custom"] &&
            searchOptions["filter_created_at_start_date_custom"] !== ""
            ? searchOptions["filter_created_at_start_time_custom"] !== ""
                ? format(new Date(searchOptions["filter_created_at_start_date_custom"]), "yyyy-MM-dd") +
                    " " +
                    format(new Date(searchOptions["filter_created_at_start_time_custom"]), "HH:mm:ss")
                : format(new Date(searchOptions["filter_created_at_start_date_custom"]), "yyyy-MM-dd") + " 00:00:00"
            : searchOptions["filter_created_at_start_date"] &&
                searchOptions["filter_created_at_start_date"] !== ""
                ? searchOptions["filter_created_at_start_date"]
                : undefined, searchOptions["filter_created_at_end_date_custom"] &&
            searchOptions["filter_created_at_end_date_custom"] !== ""
            ? searchOptions["filter_created_at_end_time_custom"] !== ""
                ? format(new Date(searchOptions["filter_created_at_end_date_custom"]), "yyyy-MM-dd") +
                    " " +
                    format(new Date(searchOptions["filter_created_at_end_time_custom"]), "HH:mm:ss")
                : format(new Date(searchOptions["filter_created_at_end_date_custom"]), "yyyy-MM-dd") + " 23:59:59"
            : searchOptions["filter_created_at_end_date"] &&
                searchOptions["filter_created_at_end_date"] !== ""
                ? searchOptions["filter_created_at_end_date"]
                : undefined, searchOptions["filter_updated_at_start_date"]
            ? searchOptions["filter_updated_at_start_time"] !== ""
                ? format(new Date(searchOptions["filter_updated_at_start_date"]), "yyyy-MM-dd") +
                    " " +
                    format(new Date(searchOptions["filter_updated_at_start_time"]), "HH:mm:ss")
                : format(new Date(searchOptions["filter_updated_at_start_date"]), "yyyy-MM-dd") + " 00:00:00"
            : undefined, searchOptions["filter_updated_at_end_date"]
            ? searchOptions["filter_updated_at_end_time"] !== ""
                ? format(new Date(searchOptions["filter_updated_at_end_date"]), "yyyy-MM-dd") +
                    " " +
                    format(new Date(searchOptions["filter_updated_at_end_time"]), "HH:mm:ss")
                : format(new Date(searchOptions["filter_updated_at_end_date"]), "yyyy-MM-dd") +
                    " 23:59:59"
            : undefined, searchOptions["filter_start_date_from"]
            ? searchOptions["filter_start_time_from"] !== ""
                ? format(new Date(searchOptions["filter_start_date_from"]), "yyyy-MM-dd") +
                    " " +
                    format(new Date(searchOptions["filter_start_time_from"]), "HH:mm:ss")
                : format(new Date(searchOptions["filter_start_date_from"]), "yyyy-MM-dd") +
                    " 00:00:00"
            : undefined, searchOptions["filter_start_date_to"]
            ? searchOptions["filter_start_time_to"] !== ""
                ? format(new Date(searchOptions["filter_start_date_to"]), "yyyy-MM-dd") +
                    " " +
                    format(new Date(searchOptions["filter_start_time_to"]), "HH:mm:ss")
                : format(new Date(searchOptions["filter_start_date_to"]), "yyyy-MM-dd") +
                    " 23:59:59"
            : undefined, searchOptions["filter_due_date_from"]
            ? searchOptions["filter_due_time_from"] !== ""
                ? format(new Date(searchOptions["filter_due_date_from"]), "yyyy-MM-dd") +
                    " " +
                    format(new Date(searchOptions["filter_due_time_from"]), "HH:mm:ss")
                : format(new Date(searchOptions["filter_due_date_from"]), "yyyy-MM-dd") +
                    " 00:00:00"
            : undefined, searchOptions["filter_due_date_to"]
            ? searchOptions["filter_due_time_to"] !== ""
                ? format(new Date(searchOptions["filter_due_date_to"]), "yyyy-MM-dd") +
                    " " +
                    format(new Date(searchOptions["filter_due_time_to"]), "HH:mm:ss")
                : format(new Date(searchOptions["filter_due_date_to"]), "yyyy-MM-dd") +
                    " 23:59:59"
            : undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, searchOptions["filter_contact_is_new"]
            ? searchOptions["filter_contact_is_new"]
            : undefined, searchOptions["filter_inbound_channels"]
            ? searchOptions["filter_inbound_channels"]
            : undefined, searchOptions["filter_outbound_channels"]
            ? searchOptions["filter_outbound_channels"]
            : undefined, searchOptions["filter_inbound_social_channels"]
            ? searchOptions["filter_inbound_social_channels"]
            : undefined, searchOptions["filter_outbound_social_channels"]
            ? searchOptions["filter_outbound_social_channels"]
            : undefined, searchOptions["filter_forwarded_call"]
            ? searchOptions["filter_forwarded_call"]
            : undefined)
            .then(response => {
            setSearchResults(searchResults.concat(response.data.tickets));
            let total = response.data.tickets.length === parseInt(response.data.meta.per_page)
                ? 100000000
                : response.data.meta.current_page === 1
                    ? response.data.tickets.length
                    : response.data.tickets.length +
                        parseInt(response.data.meta.per_page) *
                            (response.data.meta.current_page - 1);
            setSearchMeta({
                current_page: response.data.meta.current_page,
                total: total,
            });
            setShowTicketFilters(false);
        })
            .catch(error => {
            console.error(error);
        })
            .finally(() => {
            setIsLoading(false);
        });
    };
    const getCampaigns = (search, searchMeta) => {
        setIsLoading(true);
        api.GET_campaigns(10, searchMeta.current_page + 1, undefined, search, "id", "asc", roles.isAgent ? 1 : undefined, roles.isAgent ? 1 : undefined)
            .then(response => {
            setSearchResults(searchResults.concat(response.data.campaigns));
            setSearchMeta(response.data.meta);
        })
            .catch(error => {
            console.error(error);
        })
            .finally(() => {
            setIsLoading(false);
        });
    };
    const getKnowledge = (search, searchMeta) => {
        setIsLoading(true);
        api.GET_knowledgeAll(10, searchMeta.current_page + 1, "id", "asc", search)
            .then(response => {
            setSearchResults(searchResults.concat(response.data.knowledge));
            setSearchMeta(response.data.meta);
        })
            .catch(error => {
            console.error(error);
        })
            .finally(() => {
            setIsLoading(false);
        });
    };
    const getUsers = (search, searchMeta) => {
        setIsLoading(true);
        api.GET_users(10, searchMeta.current_page + 1, "id", "asc", search)
            .then(response => {
            setSearchResults(searchResults.concat(response.data.tenant_users));
            setSearchMeta(response.data.meta);
        })
            .catch(error => {
            console.error(error);
        })
            .finally(() => {
            setIsLoading(false);
        });
    };
    const debounceSearch = useCallback(debounce((search, searchMeta, searchOptions) => {
        if (searchOptions["search_for"] === "contacts")
            getContacts(search, searchMeta, searchOptions);
        else if (searchOptions["search_for"] === "tickets") {
            getTickets(search, searchMeta, searchOptions);
        }
        else if (searchOptions["search_for"] === "campaigns")
            getCampaigns(search, searchMeta);
        else if (searchOptions["search_for"] === "knowledge")
            getKnowledge(search, searchMeta);
        else if (searchOptions["search_for"] === "users")
            getUsers(search, searchMeta);
    }, 700), [tenancy.client]);
    return tenancy.client ? (_jsxs(Fragment, { children: [_jsx(IconButton, Object.assign({ edge: "start", color: "inherit", onClick: () => setMenuOpen(true), size: "large" }, { children: _jsx(SearchIcon, {}) })), _jsx(Drawer, Object.assign({ sx: { zIndex: 1300 }, anchor: "right", open: isMenuOpen, onClose: () => setMenuOpen(false) }, { children: _jsxs(Box, Object.assign({ sx: {
                        display: "flex",
                        flexDirection: "column",
                        width: isMobile ? "320px" : "500px",
                        height: "100vh",
                        paddingX: 4,
                        paddingY: 2,
                    } }, { children: [_jsxs(Box, Object.assign({ sx: {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            } }, { children: [_jsx(Box, { children: _jsx(Typography, Object.assign({ variant: "h6" }, { children: t("search.search") })) }), _jsx(Box, { children: _jsx(IconButton, Object.assign({ edge: "start", color: "inherit", onClick: () => setMenuOpen(false), style: {
                                            marginRight: "-8px",
                                        }, size: "large" }, { children: _jsx(CloseIcon, {}) })) })] })), _jsx(Box, Object.assign({ sx: {
                                paddingY: 1,
                            } }, { children: _jsx(FormControl, Object.assign({ fullWidth: true }, { children: _jsx(OutlinedInput, { autoFocus: true, placeholder: t("search.search") +
                                        " " +
                                        (searchFor === "contacts"
                                            ? t("search.contacts")
                                            : searchFor === "tickets"
                                                ? t("search.tickets")
                                                : searchFor === "campaigns"
                                                    ? t("search.campaigns")
                                                    : searchFor === "knowledge"
                                                        ? t("search.knowledge")
                                                        : t("search.users")) +
                                        (searchFor === "tickets"
                                            ? " (" +
                                                t("search.by") +
                                                " " +
                                                (searchTicketsBy === "contact.first_name"
                                                    ? t("contacts.contact-attributes.first-name").toLowerCase()
                                                    : searchTicketsBy === "contact.full_name"
                                                        ? t("contacts.contact-attributes.full-name").toLowerCase()
                                                        : searchTicketsBy ===
                                                            "contact.full_name,contact.company"
                                                            ? t("contacts.contact-attributes.full-name-company").toLowerCase()
                                                            : searchTicketsBy === "contact.last_name"
                                                                ? t("contacts.contact-attributes.last-name").toLowerCase()
                                                                : searchTicketsBy === "contact.company"
                                                                    ? t("contacts.contact-attributes.company").toLowerCase()
                                                                    : searchTicketsBy === "contact.phones"
                                                                        ? t("contacts.contact-attributes.phones").toLowerCase()
                                                                        : searchTicketsBy === "contact.emails"
                                                                            ? t("contacts.contact-attributes.emails").toLowerCase()
                                                                            : searchTicketsBy === "contact.address"
                                                                                ? t("contacts.contact-attributes.address").toLowerCase()
                                                                                : searchTicketsBy === "contact.oib"
                                                                                    ? "OIB"
                                                                                    : searchTicketsBy === "campaign.name"
                                                                                        ? t("tickets.ticket-filters.campaign-name")
                                                                                        : t("tickets.ticket").toLowerCase() + " ID") +
                                                ")"
                                            : searchFor === "contacts"
                                                ? " (" +
                                                    t("search.by") +
                                                    " " +
                                                    (searchContactsBy === "id"
                                                        ? t("contacts.contact").toLowerCase() + " ID"
                                                        : searchContactsBy === "full_name"
                                                            ? t("contacts.contact-attributes.full-name").toLowerCase()
                                                            : searchContactsBy === "full_name,company"
                                                                ? t("contacts.contact-attributes.full-name-company").toLowerCase()
                                                                : searchContactsBy === "first_name"
                                                                    ? t("contacts.contact-attributes.first-name").toLowerCase()
                                                                    : searchContactsBy === "last_name"
                                                                        ? t("contacts.contact-attributes.last-name").toLowerCase()
                                                                        : searchContactsBy === "company"
                                                                            ? t("contacts.contact-attributes.company").toLowerCase()
                                                                            : searchContactsBy === "phones"
                                                                                ? t("contacts.contact-attributes.phones").toLowerCase()
                                                                                : searchContactsBy === "emails"
                                                                                    ? t("contacts.contact-attributes.emails").toLowerCase()
                                                                                    : searchContactsBy === "type"
                                                                                        ? t("contacts.contact-attributes.type.type").toLowerCase()
                                                                                        : "OIB") +
                                                    ")"
                                                : ""), value: search, onChange: event => setSearch(event.target.value), startAdornment: _jsx(InputAdornment, Object.assign({ position: "start" }, { children: _jsx(SearchIcon, {}) })), endAdornment: _jsxs(InputAdornment, Object.assign({ position: "end" }, { children: [search.length > 0 && (_jsx(IconButton, Object.assign({ edge: "end", onClick: () => setSearch(""), size: "large" }, { children: isLoading ? (_jsx(CircularProgress, { color: "inherit", size: 18, thickness: 5, style: {
                                                        marginRight: "4px",
                                                    } })) : (_jsx(ClearIcon, {})) }))), searchFor === "tickets" && (_jsx(IconButton, Object.assign({ disabled: isLoading, edge: "end", onClick: () => setShowTicketFilters(true), size: "large" }, { children: _jsx(FilteredIcon, { color: !isLoading ? "action" : "disabled" }) }))), searchFor === "contacts" && (_jsx(IconButton, Object.assign({ disabled: isLoading, edge: "end", onClick: () => setShowContactFilters(true), size: "large" }, { children: _jsx(FilteredIcon, { color: !isLoading ? "action" : "disabled" }) })))] })) }) })) })), _jsx(Box, Object.assign({ className: classes.tabs }, { children: _jsxs(Tabs, Object.assign({ indicatorColor: "secondary", textColor: "inherit", value: searchFor, onChange: (event, value) => {
                                    setSearchResults([]);
                                    setSearchFor(value);
                                }, variant: "scrollable", scrollButtons: "auto" }, { children: [_jsx(Tab, { value: "tickets", label: t("tickets.tickets"), disabled: isLoading }), _jsx(Tab, { value: "contacts", label: t("contacts.contacts"), disabled: isLoading }), _jsx(Tab, { value: "campaigns", label: t("campaigns.campaigns"), disabled: isLoading }), _jsx(Tab, { value: "knowledge", label: t("knowledge.knowledge"), disabled: isLoading }), _jsx(Tab, { value: "users", label: t("users.users"), disabled: isLoading })] })) })), _jsx(Box, Object.assign({ className: classes.scroll, sx: {
                                display: "flex",
                                justifyContent: "center",
                                marginY: 2,
                            } }, { children: _jsx(InfiniteScroll, Object.assign({ height: window.innerHeight - 232, dataLength: searchResults.length, next: searchByScrolling, hasMore: searchResults.length < searchMeta.total, loader: _jsx(Box, Object.assign({ sx: {
                                        paddingY: 2,
                                    } }, { children: _jsx(Typography, Object.assign({ variant: "overline" }, { children: t("search.loading") })) })), endMessage: _jsx(Box, Object.assign({ sx: {
                                        paddingY: 2,
                                    } }, { children: _jsx(Typography, Object.assign({ variant: "overline" }, { children: searchResults.length === 1
                                            ? searchResults.length +
                                                " " +
                                                t("search.search-results-1")
                                            : searchResults.length +
                                                " " +
                                                t("search.search-results>1") })) })), style: {
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                    alignContent: "flex-start",
                                } }, { children: searchResults.map(data => (_jsxs(Box, Object.assign({ sx: {
                                        paddingY: 1,
                                        width: "100%",
                                    } }, { children: [searchFor === "contacts" && (_jsx(SearchContactCard, { type: "view", contact: data })), searchFor === "campaigns" && (_jsx(SearchCampaignCard, { campaign: data })), searchFor === "knowledge" && (_jsx(SearchKnowledgeCard, { knowledge: data })), searchFor === "tickets" && _jsx(SearchTicketCard, { ticket: data }), searchFor === "users" && _jsx(SearchUserCard, { user: data })] }), data.id))) })) }))] })) })), isMenuOpen &&
                (searchFor === "contacts" ? (_jsx(ContactFilters, { showFilters: showContactFilters, searchBy: searchContactsBy, setSearchBy: setSearchContactsBy, filterType: filterContactType, setFilterType: setFilterContactType, filterPhoneCountry: filterContactPhoneCountry, setFilterPhoneCountry: setFilterContactPhoneCountry, filterPhoneArea: filterContactPhoneArea, setFilterPhoneArea: setFilterContactPhoneArea, filterCustomFields: filterContactCustomFields, setFilterCustomFields: setFilterContactCustomFields, isLoading: isLoading, setFilter: setFilter, showMoreFilters: showMoreContactFilters, setShowMoreFilters: setShowMoreContactFilters, areFieldsAndGroupsLoaded: areFieldsAndGroupsLoaded, setAreFieldsAndGroupsLoaded: setAreFieldsAndGroupsLoaded, phoneCountries: phoneCountries, setPhoneCountries: setPhoneCountries, phoneAreas: phoneAreas, setPhoneAreas: setPhoneAreas, customFields: contactCustomFields, setCustomFields: setContactCustomFields, isGlobalSearch: true })) : (searchFor === "tickets" && (_jsx(TicketFilters, { type: "tickets", showFilters: showTicketFilters, searchBy: searchTicketsBy, setSearchBy: setSearchTicketsBy, filterCampaigns: filterTicketsByCampaigns, setFilterCampaigns: setFilterTicketsByCampaigns, filterContacts: filterTicketsByContacts, setFilterContacts: setFilterTicketsByContacts, filterStatuses: filterTicketsByStatuses, setFilterStatuses: setFilterTicketsByStatuses, filterPriorities: filterTicketsByPriorities, setFilterPriorities: setFilterTicketsByPriorities, filterAssignees: filterTicketsByAssignees, setFilterAssignees: setFilterTicketsByAssignees, filterGroups: filterTicketsByGroups, setFilterGroups: setFilterTicketsByGroups, filterCreatedBy: filterTicketsByCreatedBy, setFilterCreatedBy: setFilterTicketsByCreatedBy, filterUpdatedBy: filterTicketsByUpdatedBy, setFilterUpdatedBy: setFilterTicketsByUpdatedBy, filterDrafts: filterDrafts, setFilterDrafts: setFilterDrafts, filterUnassigned: filterTicketsByUnassigned, setFilterUnassigned: setFilterTicketsByUnassigned, filterUngrouped: filterTicketsByUngrouped, setFilterUngrouped: setFilterTicketsByUngrouped, filterCreatedStartDate: filterTicketsByCreatedAtStart ? filterTicketsByCreatedAtStart : "", setFilterCreatedStartDate: setFilterTicketsByCreatedAtStart, filterCreatedEndDate: filterTicketsByCreatedAtEnd ? filterTicketsByCreatedAtEnd : "", setCreatedFilterEndDate: setFilterTicketsByCreatedAtEnd, filterUpdatedStartDate: filterTicketsByUpdatedAtStartDate
                        ? filterTicketsByUpdatedAtStartDate
                        : "", filterUpdatedStartTime: filterTicketsByUpdatedAtStartTime
                        ? filterTicketsByUpdatedAtStartTime
                        : "", filterCreatedStartDateCustom: filterTicketsByCreatedAtStartDateCustom
                        ? filterTicketsByCreatedAtStartDateCustom
                        : "", filterCreatedStartTimeCustom: filterTicketsByCreatedAtStartTimeCustom
                        ? filterTicketsByCreatedAtStartTimeCustom
                        : "", setFilterCreatedStartDateCustom: setFilterTicketsByCreatedAtStartDateCustom, setFilterCreatedStartTimeCustom: setFilterTicketsByCreatedAtStartTimeCustom, filterCreatedEndDateCustom: filterTicketsByCreatedAtEndDateCustom
                        ? filterTicketsByCreatedAtEndDateCustom
                        : "", filterCreatedEndTimeCustom: filterTicketsByCreatedAtEndTimeCustom
                        ? filterTicketsByCreatedAtEndTimeCustom
                        : "", setFilterCreatedEndDateCustom: setFilterTicketsByCreatedAtEndDateCustom, setFilterCreatedEndTimeCustom: setFilterTicketsByCreatedAtEndTimeCustom, setFilterUpdatedStartDate: setFilterTicketsByUpdatedAtStartDate, setFilterUpdatedStartTime: setFilterTicketsByUpdatedAtStartTime, filterUpdatedEndDate: filterTicketsByUpdatedAtEndDate
                        ? filterTicketsByUpdatedAtEndDate
                        : "", filterUpdatedEndTime: filterTicketsByUpdatedAtEndTime
                        ? filterTicketsByUpdatedAtEndTime
                        : "", setFilterUpdatedEndDate: setFilterTicketsByUpdatedAtEndDate, setFilterUpdatedEndTime: setFilterTicketsByUpdatedAtEndTime, filterStartStartDate: filterTicketsByStartStartDate ? filterTicketsByStartStartDate : "", filterStartStartTime: filterTicketsByStartStartTime ? filterTicketsByStartStartTime : "", setFilterStartStartDate: setFilterTicketsByStartStartDate, setFilterStartStartTime: setFilterTicketsByStartStartTime, filterStartEndDate: filterTicketsByStartEndDate ? filterTicketsByStartEndDate : "", filterStartEndTime: filterTicketsByStartEndTime ? filterTicketsByStartEndTime : "", setFilterStartEndDate: setFilterTicketsByStartEndDate, setFilterStartEndTime: setFilterTicketsByStartEndTime, filterDueStartDate: filterTicketsByDueStartDate ? filterTicketsByDueStartDate : "", filterDueStartTime: filterTicketsByDueStartTime ? filterTicketsByDueStartTime : "", setFilterDueStartDate: setFilterTicketsByDueStartDate, setFilterDueStartTime: setFilterTicketsByDueStartTime, filterDueEndDate: filterTicketsByDueEndDate ? filterTicketsByDueEndDate : "", filterDueEndTime: filterTicketsByDueEndTime ? filterTicketsByDueEndTime : "", setFilterDueEndDate: setFilterTicketsByDueEndDate, setFilterDueEndTime: setFilterTicketsByDueEndTime, filterAudiences: [], setFilterAudiences: () => [], filterCustomFields: filterTicketCustomFields, setFilterCustomFields: setFilterTicketCustomFields, filterCampaignChoices: filterCampaignChoices, setFilterCampaignChoices: setFilterCampaignChoices, filterContactIsNew: filterContactIsNew, setFilterContactIsNew: setFilterContactIsNew, filterInboundChannels: filterInboundChannels, setFilterInboundChannels: setFilterInboundChannels, filterOutboundChannels: filterOutboundChannels, setFilterOutboundChannels: setFilterOutboundChannels, filterInboundSocialChannels: filterInboundSocialChannels, setFilterInboundSocialChannels: setFilterInboundSocialChannels, filterOutboundSocialChannels: filterOutboundSocialChannels, setFilterOutboundSocialChannels: setFilterOutboundSocialChannels, filterForwardedCalls: filterForwardedCalls, setFilterForwardedCalls: setFilterForwardedCalls, timeFrame: timeFrame, setTimeFrame: setTimeFrame, user: undefined, isLoading: isLoading, setFilter: setFilter, showMoreFilters: showMoreTicketFilters, setShowMoreFilters: setShowMoreTicketFilters, showFieldFilters: showFieldFilters, setShowFieldFilters: setShowFieldFilters, showChannelFilters: showChannelFilters, setShowChannelFilters: setShowChannelFilters, areFieldsAndGroupsLoaded: areFieldsAndGroupsLoaded, setAreFieldsAndGroupsLoaded: setAreFieldsAndGroupsLoaded, filterableChoices: filterableChoices, setFilterableChoices: setFilterableChoices, customFields: customFields, setCustomFields: setCustomFields, groups: groups, setGroups: setGroups, campaigns: campaigns, setCampaigns: setCampaigns, audiences: audiences, setAudiences: setAudiences, isGlobalSearch: true }))))] })) : (_jsx(Fragment, {}));
}
